import React from 'react';
import { HeatTier } from '../utils/heat';
import { sparkPath } from '../utils/spark';

interface HeatSparkProps {
  /** Messages per bucket over the last five minutes, oldest first */
  history: number[];
  tier: HeatTier;
  /** False while the 5-minute baseline is still warming up */
  ready: boolean;
  width?: number;
  height?: number;
  /** How many trailing buckets make up the live edge (2 × 5 s = the 10 s window) */
  liveBuckets?: number;
}

/**
 * The seismograph: five minutes of one chat, drawn on the thermal ramp. The
 * trailing ten seconds sit on a lit strip — that slice is what the heat
 * ratio holds against the rest of the trace.
 */
export const HeatSpark: React.FC<HeatSparkProps> = ({
  history,
  tier,
  ready,
  width = 96,
  height = 22,
  liveBuckets = 2,
}) => {
  const { line, area } = sparkPath(history, width, height, 1.5);
  const liveWidth = history.length > 1 ? (width / (history.length - 1)) * liveBuckets : 0;
  const total = history.reduce((sum, n) => sum + n, 0);

  return (
    <svg
      className={`heat-spark heat-${tier}${ready ? '' : ' calibrating'}`}
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      preserveAspectRatio="none"
      role="img"
      aria-label={ready ? `Chat over the last five minutes: ${total} messages` : 'Measuring chat baseline'}
    >
      {liveWidth > 0 && (
        <rect className="spark-now" x={width - liveWidth} y={0} width={liveWidth} height={height} />
      )}
      {area && <path className="spark-area" d={area} />}
      {line && <path className="spark-line" d={line} vectorEffect="non-scaling-stroke" />}
    </svg>
  );
};
